<template>
    <div id="list-apiary-equipment-component" class="pa-4">
        <template v-if="!loading_apiary">
            <p class="font-weight-bold">
                {{ $tc('global_apiary.you_have_x_apiaries', apiaries.length) }}
            </p>

            <v-row class="mb-2">
                <v-col v-for="(apiary, index) in apiaries" :key="apiary.id_apiary" cols="12" lg="4" sm="6" xl="3">
                    <div class="pointer h-100" @click="displayDetailApiary(index)">
                        <ApiaryCardMinimalistComponent
                            :apiary="apiary"
                            :is_selected="index === index_apiary_selected"
                            v-on:refreshApiaries="getClientsApiaryByIdClient"
                        />
                    </div>
                </v-col>

                <v-col v-if="userHasAccess(asset_module.designation, role_edit)" cols="12" lg="4" sm="6" xl="3">
                    <a class="add-asset-btn" target="_blank" @click="$refs.addEditApiaryDialog.openDialogAddEditApiary(null)">
                        <v-icon class="font-color-medium">mdi-plus</v-icon>
                        <span class="font-color-medium text-uppercase">{{ $t('global.add') }}</span>
                    </a>
                </v-col>
            </v-row>

            <template v-if="apiaries[index_apiary_selected]">
                <div class="mb-6">
                    <v-row>
                        <v-col cols="12" md="6">
                            <ApiaryInformationWidgetComponent :apiary="apiaries[index_apiary_selected]" />
                        </v-col>

                        <v-col cols="12" md="6">
                            <ApiaryHivesChartWidgetComponent :apiary="apiaries[index_apiary_selected]" />
                        </v-col>

                        <v-col cols="12" md="6">
                            <ApiaryHivesLocalisationWidgetComponent :hives="apiaries[index_apiary_selected].hives" />
                        </v-col>

                        <v-col cols="12" md="6">
                            <ApiaryWeatherWidgetComponent :apiary="apiaries[index_apiary_selected]" />
                        </v-col>
                    </v-row>
                </div>

                <div class="mb-4">
                    <p class="font-weight-bold">
                        {{ $tc('global_apiary.you_have_x_apiary_hives', apiaries[index_apiary_selected].hives.length) }}
                    </p>

                    <v-row>
                        <v-col
                            v-for="(hive, index) in apiaries[index_apiary_selected].hives"
                            :key="hive.id_asset"
                            cols="4"
                            lg="4"
                            md="6"
                            xl="2"
                        >
                            <div class="h-100" @click="displayDetailAsset(index)">
                                <AssetGenericCardMinimalistComponent
                                    :asset="hive"
                                    :asset_module="asset_module"
                                    :is_selected="index === index_asset_selected"
                                />
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <div v-if="apiaries[index_apiary_selected]?.hives[index_asset_selected]">
                    <AssetDetailsComponent
                        :asset="apiaries[index_apiary_selected].hives[index_asset_selected]"
                        :asset_module="asset_module"
                    />
                </div>
            </template>
        </template>

        <template v-else>
            <v-row>
                <v-col v-for="n in 3" :key="n" class="mt-2" cols="4" lg="3" md="4" xl="2">
                    <div class="h-100">
                        <v-skeleton-loader v-if="is_mobile" type="text" />
                        <v-skeleton-loader v-else type="image" />
                    </div>
                </v-col>
            </v-row>

            <v-row class="mt-8">
                <v-col v-for="n in 4" :key="n" cols="12" lg="6">
                    <v-skeleton-loader type="card-heading, list-item-two-line, image, table-thead" />
                </v-col>
            </v-row>

            <v-row>
                <v-col v-for="n in 3" :key="n" class="mt-2" cols="4" lg="3" md="4" xl="2">
                    <div class="h-100">
                        <v-skeleton-loader v-if="is_mobile" type="text" />
                        <v-skeleton-loader v-else type="image" />
                    </div>
                </v-col>
            </v-row>
        </template>

        <AddEditApiaryDialogComponent ref="addEditApiaryDialog" v-on:apiaryAdded="addApiary" />
    </div>
</template>

<script>
import ClientRepository from '@/repositories/ClientRepository'
import ApiaryCardMinimalistComponent from '@/components/equipment/apiary/vision/ApiaryCardMinimalistComponent.vue'
import AssetGenericCardMinimalistComponent from '@/components/equipment/generic/vision/AssetGenericCardMinimalistComponent.vue'
import ApiaryHivesLocalisationWidgetComponent from '@/components/equipment/apiary/widget/ApiaryHivesLocalisationWidgetComponent.vue'
import ApiaryInformationWidgetComponent from '@/components/equipment/apiary/widget/ApiaryInformationWidgetComponent.vue'
import AddEditApiaryDialogComponent from '@/components/equipment/apiary/form/AddEditApiaryDialogComponent.vue'
import ApiaryWeatherWidgetComponent from '@/components/equipment/apiary/widget/ApiaryWeatherWidgetComponent.vue'
import AssetDetailsComponent from '@/components/equipment/generic/vision/AssetDetailsComponent.vue'
import ApiaryHivesChartWidgetComponent from '@/components/equipment/apiary/widget/ApiaryHivesChartWidgetComponent.vue'

export default {
    name: 'ListApiaryEquipmentComponent',
    components: {
        ApiaryHivesChartWidgetComponent,
        AssetDetailsComponent,
        ApiaryWeatherWidgetComponent,
        AddEditApiaryDialogComponent,
        ApiaryInformationWidgetComponent,
        ApiaryHivesLocalisationWidgetComponent,
        AssetGenericCardMinimalistComponent,
        ApiaryCardMinimalistComponent,
    },
    data() {
        return {
            asset_module: null,
            loading_apiary: false,
            apiaries: [],
            index_apiary_selected: 0,
            index_asset_selected: 0,
        }
    },
    created() {
        this.asset_module = this.$store.getters['asset_module/asset_modules'].find((module) => module.designation === 'GNC_HIVE')

        this.getClientsApiaryByIdClient()
    },
    methods: {
        getClientsApiaryByIdClient() {
            this.loading_apiary = true

            ClientRepository.getClientsApiaryByIdClient(this.$store.getters['user_connected/user_connected'].id_client)
                .then((response) => {
                    this.apiaries = response.data.data
                })
                .catch((error) => {
                    this.manageError(error)
                })
                .finally(() => {
                    this.loading_apiary = false
                })
        },

        displayDetailApiary(indexAssetSelected) {
            this.index_apiary_selected = indexAssetSelected
            setTimeout(() => {
                this.reload = false
            }, 50)
        },

        addApiary(apiary) {
            this.apiaries.push(apiary)
            this.index_apiary_selected = this.apiaries.length - 1
        },

        displayDetailAsset(index) {
            this.index_asset_selected = index
            this.$store.commit('asset_module/refreshAssetPage')
        },
    },
}
</script>
